import { z } from 'zod';

import { type CoreProductFrontendFilterOptionsMap } from '../CoreGeneralFilters.types';

import { frontendFilterOptionIdSchema } from './shared.schema';

const productAttributePayloadSchema = z.object({
  id: z.string(),
  description: z.string(),
});

const productSeasonEventPayloadSchema = z.object({
  season: productAttributePayloadSchema,
  event: productAttributePayloadSchema,
});

const productWithParentIdPayloadSchema = z.object({
  id: z.string(),
  department_id: z.string().nullable(),
  department_desc: z.string().nullable(),
  sub_department_id: z.string().nullable(),
  sub_department_desc: z.string().nullable(),
  style_id: z.string().nullable(),
  style_desc: z.string().nullable(),
  brand_id: z.string().nullable(),
  brand_desc: z.string().nullable(),
  merchandising_flag: z.string().nullable(),
  color_id: z.string().nullable(),
  color_desc: z.string().nullable(),
  manufacturer_id: z.string().nullable(),
  manufacturer_desc: z.string().nullable(),
  budget_level: z.string().nullable(),
  class_id: z.string().nullable(),
  class_desc: z.string().nullable(),
  sub_class_id: z.string().nullable(),
  sub_class_desc: z.string().nullable(),
  article_id: z.string().nullable(),
  article_desc: z.string().nullable(),
  material_id: z.string().nullable(),
  material_desc: z.string().nullable(),
  gender_id: z.string(),
  gender_desc: z.string(),
  image_url: z.string().nullable(),
  parent_product_id: z.string().nullable(),
});

export const coreProductsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products'),
  payload: productWithParentIdPayloadSchema,
});

export const coreProductsDepartmentsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/departments'),
  payload: productAttributePayloadSchema,
});

export const coreProductsSubDepartmentsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/sub-departments'),
  payload: productAttributePayloadSchema,
});

export const coreProductsSeasonsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/seasons'),
  payload: productAttributePayloadSchema,
});

export const coreProductsCollectionTypesFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/collection-types'),
  payload: productAttributePayloadSchema,
});

export const coreProductsEventsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/events'),
  payload: productAttributePayloadSchema,
});

export const coreProductsBrandsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/brands'),
  payload: productAttributePayloadSchema,
});

export const coreProductsSeasonEventsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/season-events'),
  payload: productSeasonEventPayloadSchema,
});

export const coreProductsBudgetLevelsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/budget-levels'),
  payload: productAttributePayloadSchema,
});

export const coreProductsClassesFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/classes'),
  payload: productAttributePayloadSchema,
});

export const coreProductsSubClassesFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/sub-classes'),
  payload: productAttributePayloadSchema,
});

export const coreProductsManufacturersFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/manufacturers'),
  payload: productAttributePayloadSchema,
});

export const coreProductsArticlesFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/articles'),
  payload: productAttributePayloadSchema,
});

export const coreProductsMaterialsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/materials'),
  payload: productAttributePayloadSchema,
});

export const coreProductsStylesFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/styles'),
  payload: productAttributePayloadSchema,
});

export const coreProductsColorsFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/colors'),
  payload: productAttributePayloadSchema,
});

export const coreProductsGendersFilterOptionSchema = z.object({
  id: frontendFilterOptionIdSchema,
  endpoint: z.literal('/core/products/genders'),
  payload: productAttributePayloadSchema,
});

type CoreProductFrontendFilterOptionZodSchemaMap = Required<{
  [K in keyof CoreProductFrontendFilterOptionsMap]: z.ZodSchema<
    NonNullable<CoreProductFrontendFilterOptionsMap[K]>[number]
  >;
}>;

export const coreProductsFilterOptionZodSchemaMap = {
  products: coreProductsFilterOptionSchema,
  departments: coreProductsDepartmentsFilterOptionSchema,
  sub_departments: coreProductsSubDepartmentsFilterOptionSchema,
  seasons: coreProductsSeasonsFilterOptionSchema,
  collection_types: coreProductsCollectionTypesFilterOptionSchema,
  events: coreProductsEventsFilterOptionSchema,
  brands: coreProductsBrandsFilterOptionSchema,
  season_events: coreProductsSeasonEventsFilterOptionSchema,
  budget_levels: coreProductsBudgetLevelsFilterOptionSchema,
  classes: coreProductsClassesFilterOptionSchema,
  sub_classes: coreProductsSubClassesFilterOptionSchema,
  manufacturers: coreProductsManufacturersFilterOptionSchema,
  articles: coreProductsArticlesFilterOptionSchema,
  materials: coreProductsMaterialsFilterOptionSchema,
  styles: coreProductsStylesFilterOptionSchema,
  colors: coreProductsColorsFilterOptionSchema,
  genders: coreProductsGendersFilterOptionSchema,
} satisfies CoreProductFrontendFilterOptionZodSchemaMap;
